import React, { useState, useEffect } from 'react';
import { Product, SellingModel, ProductVariant, Review, User, FlashSale } from '../types';
import { 
  ChevronLeft, 
  Star, 
  ShoppingCart, 
  ShieldCheck, 
  Truck, 
  RefreshCw, 
  Heart, 
  Share2, 
  Film, 
  Image as ImageIcon, 
  ExternalLink, 
  Info, 
  Tag,
  MessageSquare,
  User as UserIcon,
  CheckCircle,
  ThumbsUp,
  MoreVertical,
  Plus,
  XCircle,
  Loader2,
  ArrowRight,
  Zap,
  Maximize
} from 'lucide-react';
import { MOCK_CATEGORIES } from '../lib/mockDb';
import { affiliateService } from '../services/affiliateService';
import { authService } from '../services/authService';
import { behaviorService } from '../services/behaviorService';
import { storage } from '../lib/storage';
import { useCurrency } from '../contexts/CurrencyContext';
import { flashSaleService } from '../services/flashSaleService';
import ProductBundles from '../components/ProductBundles';
import ARViewModal from '../components/ARViewModal';

interface ProductDetailProps {
  product: Product;
  onAddToCart: (product: Product, variant?: ProductVariant) => void;
  onBack: () => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ product, onAddToCart, onBack }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [showAR, setShowAR] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [activeSale, setActiveSale] = useState<FlashSale | null>(null);
  const { format } = useCurrency();
  
  // Reviews State
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);

  // Variant Selection State
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [activeVariant, setActiveVariant] = useState<ProductVariant | null>(null);

  useEffect(() => {
    const init = async () => {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      
      // Track View Behavior
      behaviorService.track(currentUser?.id || 'guest-session', product.id, 'VIEW');

      const allReviews = storage.getReviews(product.id);
      setReviews(allReviews);

      const sale = flashSaleService.getActiveSale(product.storeId);
      if (sale && sale.productIds.includes(product.id)) {
        setActiveSale(sale);
      }

      if (currentUser) {
        const orders = storage.getOrders().filter(o => o.userId === currentUser.id);
        const purchased = orders.some(o => o.items?.some(i => i.productId === product.id));
        setHasPurchased(purchased);
      }
    };
    init();

    if (product.attributes && product.attributes.length > 0) {
      const initial: Record<string, string> = {};
      product.attributes.forEach(attr => {
        initial[attr.name] = attr.options[0];
      });
      setSelectedAttributes(initial);
    }
  }, [product]);

  useEffect(() => {
    if (product.variants && product.variants.length > 0) {
      const found = product.variants.find(v => {
        return Object.entries(selectedAttributes).every(([key, value]) => v.attributes[key] === value);
      });
      setActiveVariant(found || null);
    }
  }, [selectedAttributes, product.variants]);

  const handleAttributeChange = (name: string, value: string) => {
    setSelectedAttributes(prev => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsAdding(true);
    onAddToCart(product, activeVariant || undefined);
    behaviorService.track(user?.id || 'guest-session', product.id, 'ADD_TO_CART');
    setTimeout(() => setIsAdding(false), 1000);
  };

  const handleOpenAR = () => {
    setShowAR(true);
    behaviorService.track(user?.id || 'guest-session', product.id, 'AR_TRY_ON');
  };

  const handleAffiliateBuy = async () => {
    const currentUser = await authService.getCurrentUser();
    affiliateService.trackClick(product, currentUser);
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return alert("Please sign in to leave a review.");
    if (!newReview.comment.trim()) return alert("Please enter a comment.");

    setIsSubmitting(true);
    // Fix: Block-scoped variable 'review' used before its declaration. 
    // Changed 'review.comment' (referencing the variable being defined) to 'newReview.comment'.
    const review: Review = {
      id: 'rev-' + Date.now(),
      productId: product.id,
      userId: user.id,
      userName: user.email.split('@')[0],
      rating: newReview.rating,
      comment: newReview.comment,
      isVerified: hasPurchased,
      createdAt: new Date().toISOString()
    };

    storage.saveReview(review);
    setReviews([review, ...reviews]);
    setShowReviewForm(false);
    setNewReview({ rating: 5, comment: '' });
    setIsSubmitting(false);
  };

  const categoryName = MOCK_CATEGORIES.find(c => c.id === product.categoryId)?.name || 'Gadget';
  const isAffiliate = product.sellingModel === SellingModel.AFFILIATE;
  
  const basePrice = activeVariant ? activeVariant.price : product.price;
  const finalPrice = activeSale ? basePrice * (1 - activeSale.discountPercentage / 100) : basePrice;
  const displayStock = activeVariant ? activeVariant.stock : product.stock;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in duration-500 pb-32">
      {showAR && <ARViewModal product={product} onClose={() => setShowAR(false)} />}
      
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors mb-8 font-medium group"
      >
        <ChevronLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
        Back to Products
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20 mb-24">
        <div className="space-y-6">
          <div className="relative aspect-square bg-gray-100 rounded-[40px] overflow-hidden border border-gray-100 shadow-inner group">
            {showVideo && product.videoUrl ? (
               <video 
                 src={product.videoUrl} 
                 className="w-full h-full object-cover" 
                 autoPlay 
                 loop 
                 muted 
                 playsInline
               />
            ) : (
              <div className="w-full h-full relative">
                <img 
                  key={selectedImage}
                  src={product.images[selectedImage]} 
                  alt={product.name} 
                  className="w-full h-full object-cover transition-all duration-700 animate-in fade-in zoom-in-95" 
                />
              </div>
            )}
            
            <div className="absolute bottom-6 right-6 flex flex-col gap-3 z-10">
              {product.videoUrl && (
                <button 
                  onClick={() => setShowVideo(!showVideo)}
                  className="bg-white/90 backdrop-blur px-6 py-3 rounded-2xl flex items-center gap-2 shadow-2xl hover:scale-105 active:scale-95 transition-all"
                >
                  {showVideo ? <ImageIcon className="h-5 w-5 text-blue-600" /> : <Film className="h-5 w-5 text-blue-600" />}
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-900">
                    {showVideo ? 'Photos' : 'AI Video'}
                  </span>
                </button>
              )}
              <button 
                onClick={handleOpenAR}
                className="bg-indigo-600 text-white px-6 py-3 rounded-2xl flex items-center gap-2 shadow-2xl hover:scale-105 active:scale-95 transition-all"
              >
                <Maximize className="h-5 w-5" />
                <span className="text-[10px] font-black uppercase tracking-widest">AR Try-On</span>
              </button>
            </div>

            {activeSale && (
              <div className="absolute top-8 left-8 z-10 animate-in zoom-in duration-700">
                 <div className="bg-orange-600 text-white px-6 py-3 rounded-3xl flex flex-col items-center gap-1 shadow-2xl">
                    <div className="flex items-center gap-2">
                       <Zap className="h-4 w-4 fill-current" />
                       <span className="text-[10px] font-black uppercase tracking-widest">FLASH SALE LIVE</span>
                    </div>
                    <p className="text-xl font-black italic">-{activeSale.discountPercentage}% OFF</p>
                 </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {product.images.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => { setSelectedImage(idx); setShowVideo(false); }}
                className={`w-24 h-24 rounded-2xl overflow-hidden border-2 transition-all flex-shrink-0 ${
                  selectedImage === idx && !showVideo ? 'border-blue-600 ring-4 ring-blue-50 scale-105' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-105'
                }`}
              >
                <img src={img} className="w-full h-full object-cover" alt="" />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-brand-primary font-bold text-sm uppercase tracking-widest">{categoryName}</span>
              <div className="flex gap-2">
                <button className="p-2.5 rounded-full border border-gray-200 text-gray-400 hover:text-red-500 transition-all">
                  <Heart className="h-5 w-5" />
                </button>
                <button className="p-2.5 rounded-full border border-gray-200 text-gray-400 hover:text-blue-500 transition-all">
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">{product.name}</h1>
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-1 text-yellow-500 font-bold">
                {[1,2,3,4,5].map(i => (
                  <Star 
                    key={i} 
                    className={`h-4 w-4 ${i <= (product.rating || 5) ? 'fill-current' : 'text-gray-200'}`} 
                  />
                ))}
                <span className="ml-2 text-gray-400 text-sm font-medium">
                  {product.rating?.toFixed(1) || '5.0'} ({product.reviewCount || 0} reviews)
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-baseline gap-4">
               <div className="flex flex-col">
                  {activeSale && (
                    <p className="text-xl font-bold text-gray-400 line-through italic">{format(basePrice)}</p>
                  )}
                  <p className={`text-5xl font-black ${activeSale ? 'text-orange-600' : 'text-gray-900'} tracking-tighter`}>{format(finalPrice)}</p>
               </div>
               {!isAffiliate && (
                 <p className={`text-xs font-black uppercase tracking-widest ${displayStock > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                   {displayStock > 0 ? `${displayStock} Units Ready` : 'Sold Out'}
                 </p>
               )}
            </div>
            <p className="text-gray-500 text-lg leading-relaxed">
              {product.description}
            </p>
          </div>

          <div className="pt-6">
            {isAffiliate ? (
              <div className="space-y-6">
                 <button 
                   onClick={handleAffiliateBuy}
                   className="w-full py-6 rounded-3xl font-black text-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center justify-center gap-3 transition-all shadow-2xl hover:scale-[1.02]"
                 >
                   Buy on {product.affiliatePlatform || 'Partner Store'} <ExternalLink className="h-6 w-6" />
                 </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button 
                  onClick={handleAdd}
                  disabled={isAdding || displayStock === 0}
                  className={`flex-grow py-5 rounded-3xl font-black text-lg flex items-center justify-center gap-3 transition-all shadow-xl ${
                    isAdding ? 'bg-green-500 text-white scale-95' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                  }`}
                >
                  {isAdding ? 'Added!' : <><ShoppingCart className="h-6 w-6" /> Add to Cart</>}
                </button>
                <button disabled={displayStock === 0} className="flex-grow py-5 rounded-3xl font-black text-lg bg-gray-900 text-white hover:bg-black transition-all shadow-xl disabled:opacity-50">
                  Buy Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProductBundles product={product} onAddToCart={onAddToCart} />

      {/* Reviews Section */}
      <section className="mt-24 space-y-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-gray-100 pb-10">
          <div className="space-y-2">
            <h2 className="text-3xl font-black text-gray-900 tracking-tight">Customer Intel</h2>
            <div className="flex items-center gap-4">
               <div className="flex items-center gap-1 text-yellow-500">
                  {[1,2,3,4,5].map(i => <Star key={i} className={`h-5 w-5 ${i <= (product.rating || 5) ? 'fill-current' : 'text-gray-200'}`} />)}
               </div>
               <span className="text-lg font-black text-gray-900">{product.rating?.toFixed(1) || '5.0'} / 5.0</span>
               <span className="text-gray-400 text-sm font-bold uppercase tracking-widest">{product.reviewCount || 0} REVIEWS</span>
            </div>
          </div>
          <button 
            onClick={() => setShowReviewForm(!showReviewForm)}
            className="flex items-center gap-2 bg-blue-50 text-blue-600 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-100 transition-all"
          >
            {showReviewForm ? <XCircle className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showReviewForm ? 'Cancel Review' : 'Write a Review'}
          </button>
        </div>

        {showReviewForm && (
          <form onSubmit={handleReviewSubmit} className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-2xl space-y-8 animate-in slide-in-from-top-4">
             <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Rate this Gadget</label>
                <div className="flex gap-4">
                   {[1,2,3,4,5].map((star) => (
                     <button 
                       key={star} 
                       type="button"
                       onClick={() => setNewReview({...newReview, rating: star})}
                       className={`p-3 rounded-2xl transition-all ${newReview.rating >= star ? 'bg-yellow-50 text-yellow-500 scale-110 shadow-lg' : 'bg-gray-50 text-gray-300 hover:text-yellow-200'}`}
                     >
                       <Star className={`h-8 w-8 ${newReview.rating >= star ? 'fill-current' : ''}`} />
                     </button>
                   ))}
                </div>
             </div>
             <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Your Feedback</label>
                <textarea 
                  rows={4} 
                  value={newReview.comment}
                  onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                  placeholder="What was your experience with this tech?"
                  className="w-full px-6 py-4 bg-gray-50 border-none rounded-[2rem] focus:ring-2 focus:ring-blue-500 font-bold resize-none"
                />
             </div>
             <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 bg-blue-600 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest shadow-2xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
             >
                {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Publish Review <ArrowRight className="h-4 w-4" /></>}
             </button>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
           {reviews.map((review) => (
             <div key={review.id} className="bg-white p-8 rounded-[40px] border border-gray-50 shadow-sm hover:shadow-xl transition-all group">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-gray-400" />
                     </div>
                     <div>
                        <p className="font-black text-gray-900">{review.userName}</p>
                        <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">{new Date(review.createdAt).toLocaleDateString()}</p>
                     </div>
                  </div>
                  {review.isVerified && (
                    <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border border-emerald-100">
                       <CheckCircle className="h-3 w-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="flex gap-1 mb-4 text-yellow-500">
                   {[1,2,3,4,5].map(i => <Star key={i} className={`h-4 w-4 ${i <= review.rating ? 'fill-current' : 'text-gray-200'}`} />)}
                </div>
                <p className="text-gray-600 text-sm font-medium leading-relaxed mb-6 italic">"{review.comment}"</p>
             </div>
           ))}
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;